import type { Task } from "@app/core";
import { Button, Card, ChevronDown, Moon, cn } from "@app/ui";

import { AnimatePresence, motion, springs, useReducedMotion } from "../../lib/motion/index.js";
import { useI18n } from "../../lib/useI18n.js";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import { useTaskAccent } from "./useTaskAccent.js";

interface Props {
  items: Task[];
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onEdit: (task: Task) => void;
  onWake: (task: Task) => void;
}

/**
 * The Snoozed module: collapsible list of sleeping tasks, soonest wake first.
 * Rows match the agenda-row affordances — task-colored accent bar and
 * click-to-edit — plus the moon disc and a "Wake now" button.
 */
export function SnoozedSection({ items, collapsed, onToggleCollapsed, onEdit, onWake }: Props) {
  const { t } = useI18n();

  return (
    <section data-today-section="snoozed" className="scroll-mt-4">
      <div className="mb-2 flex items-center gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Moon className="h-4 w-4 text-purple-500" aria-hidden /> {t("today.snoozed")}
          <span className="rounded-full bg-purple-500/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-purple-600 dark:text-purple-300">
            {items.length}
          </span>
        </h2>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
          title={collapsed ? t("today.expandSection") : t("today.collapseSection")}
          aria-label={collapsed ? t("today.expandSection") : t("today.collapseSection")}
          aria-expanded={!collapsed}
          onClick={onToggleCollapsed}
        >
          <ChevronDown className={cn("h-4 w-4 transition-transform motion-reduce:transition-none", collapsed && "-rotate-90")} />
        </Button>
      </div>
      {!collapsed && (
        <Card className="overflow-hidden p-0">
          <ul className="flex flex-col divide-y divide-border text-sm">
            <AnimatePresence initial={false}>
              {items.map((task) => (
                <SnoozedRow key={task.id} task={task} onEdit={onEdit} onWake={onWake} />
              ))}
            </AnimatePresence>
          </ul>
        </Card>
      )}
    </section>
  );
}

function SnoozedRow({ task, onEdit, onWake }: { task: Task; onEdit: (t: Task) => void; onWake: (t: Task) => void }) {
  const { t } = useI18n();
  const tf = useTimeFormat();
  const reduce = useReducedMotion();
  const accent = useTaskAccent(task);

  return (
    <motion.li
      layout={!reduce}
      initial={reduce ? false : { opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduce ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0, x: -16 }}
      transition={reduce ? { duration: 0 } : springs.smooth}
      className="group relative flex items-center gap-2 py-2 pl-4 pr-2 transition-colors hover:bg-accent/40 sm:gap-3"
    >
      <span
        aria-hidden
        className="absolute inset-y-1.5 left-1 w-[3px] rounded-full opacity-60"
        style={{ background: accent }}
      />
      <button
        type="button"
        onClick={() => onEdit(task)}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        title={t("agenda.clickToEdit")}
      >
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-purple-500/10">
          <Moon className="h-3.5 w-3.5 text-purple-500 dark:text-purple-300" aria-hidden />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="break-words whitespace-normal sm:truncate">
            {task.title || <em className="text-muted-foreground">{t("agenda.untitled")}</em>}
          </span>
          {task.snoozed_until && (
            <span className="break-words text-xs text-muted-foreground sm:truncate">
              {t("today.wakes", { time: tf.stamp(task.snoozed_until) })}
            </span>
          )}
        </span>
      </button>
      <Button
        variant="outline"
        size="sm"
        className="h-auto max-w-[42%] shrink-0 whitespace-normal px-2 py-1.5 text-center leading-tight sm:max-w-none sm:px-3 sm:whitespace-nowrap"
        onClick={() => onWake(task)}
        title={t("today.wakeNow")}
      >
        {t("today.wakeNow")}
      </Button>
    </motion.li>
  );
}
